<script setup lang="ts">
import type { CheckoutStatus } from '@/lib/params'

defineProps<{
  variant: CheckoutStatus
  title: string
  body: string
}>()
</script>

<template>
  <section class="status-card" :class="`status-card--${variant}`" role="status" aria-live="polite">
    <div class="status-card__icon">
      <svg
        v-if="variant === 'success'"
        class="status-card__glyph"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M5 12.5l4.5 4.5L19 7.5"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <svg
        v-else-if="variant === 'cancel'"
        class="status-card__glyph"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M7 7l10 10M17 7L7 17"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <svg v-else class="status-card__glyph" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M12 7v6M12 17v.5"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
    <h1 class="status-card__title">{{ title }}</h1>
    <p class="status-card__body">{{ body }}</p>
  </section>
</template>

<style lang="scss" scoped>
.status-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: space(4);
  padding: space(8) space(6);
  background: color('white');
  border-radius: $radius-lg;
  box-shadow: $shadow-md;
  max-width: 420px;
  width: 100%;

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 80px;
    border-radius: 50%;
  }

  &--success &__icon {
    background: color('success-light');
    color: color('success');
  }

  &--cancel &__icon {
    background: color('error-light');
    color: color('error');
  }

  &--unknown &__icon {
    background: color('warning-light');
    color: color('warning');
  }

  &__glyph {
    width: 44px;
    height: 44px;
  }

  &__title {
    font-size: 1.5rem;
    font-weight: 700;
    color: color('primary');
    margin: 0;
  }

  &__body {
    font-size: 1rem;
    color: color('grayblue-400');
    margin: 0;
    line-height: 1.5;
  }
}
</style>
